import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { paths } from 'src/paths';
import {
  approveRecheckDischargeAction,
  recheckDischargeOverrideStatusAction,
  resetApproveRecheckDischarge,
} from 'src/redux/actions/documentationAction';
import MissingDocsList from '../document-management/missing-docs-list';
import ReCheckIcon from './Frame 45111977.png';
import { eocTestId } from './test-ids';

type Props = {
  handleDelete: any;
  title: string;
  message: string;
  buttontext?: string;
  episodeOfCareId?: string;
  requiredDocument: boolean;
  document: any;
  source?: string;
  discipline?: string;
  isRecheckFlow?: boolean;
};

const DischargeRecheckModal = (props: Props) => {
  const {
    handleDelete,
    title,
    message,
    buttontext,
    episodeOfCareId,
    requiredDocument,
    document,
    source,
    discipline,
    isRecheckFlow,
  } = props;
  const { patients, patientDetails, episodeOfCare, details, createDocument } = paths;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeTab = useSelector((state: any) => state.switchDisciplineReducer);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );
  const overrideStatusResponse = useSelector(
    (state: any) => state?.recheckDischargeOverrideStatusReducer?.response
  );
  const approveState = useSelector((state: any) => state?.approveRecheckDischargeReducer);
  const approvalStatus = useSelector(
    (state: any) => state?.checkPendingDocsAlert?.response?.recheckDischargeApproval?.status
  );
  const approvedBy = useSelector(
    (state: any) => state?.checkPendingDocsAlert?.response?.recheckDischargeApproval?.approvedBy
  );
  // Already approved on the BE — show a read-only view with just a "Got it" button
  // (no acknowledgement checkbox, no Approve action).
  const isApproved = approvalStatus === 'APPROVED';
  const requireRecheck = permissionData?.currentPermissions?.includes(
    'Override ReCheck for Discharge'
  );
  // THIN: For the EOC discharge flow, the BE decides whether the provider is still
  const overrideStatus =
    typeof overrideStatusResponse === 'boolean'
      ? overrideStatusResponse
      : overrideStatusResponse?.data;
  // THIN-7875: the BE's word on override is not EOC-specific — GET /recheck-discharge-override-status
  // takes no arguments and answers for the caller in the header facility — so it governs the
  // dashboard entry point too. Gating it on source left the dashboard trusting the profile
  // permission alone.
  const overrideDisallowed = overrideStatus === false;
  const canOverride = requireRecheck && !overrideDisallowed;
  const alertDocuments = useSelector(
    (state: any) => state?.checkPendingDocsAlert?.response?.documents
  );
  // THIN-7674: a missing DOCUMENT is what the ReCheck approval exists to gate — nothing was
  // written, so there is a real gap to review. A missing SIGNATURE on an already-submitted
  // document is not: it is tracked and chased on its own signing queues, and holding the
  // discharge for one strands the episode. Same `submissionId` discriminator MissingDocsList
  // uses to label rows ("Missing Document" vs "Missing … Signature"), which maps onto the two
  // branches of the BE getPendingOrOverdueForms query.
  //
  // Undefined (not yet loaded) is deliberately NOT treated as "nothing blocking" — that would
  // let a user through on a slow response before the blocking rows arrive.
  const alertsLoaded = Array.isArray(alertDocuments);
  const hasBlockingDocAlert = alertsLoaded && alertDocuments.some((doc: any) => !doc?.submissionId);
  // THIN-7873: an empty list is its own case and must not borrow the signature-only copy. The BE
  // now drops an overdue recert/WPN from this list entirely when no submitted note with minutes
  // followed its due date, so "nothing outstanding" went from rare to the common outcome — and
  // "these signatures are still outstanding" pointing at an empty table is simply false. It was
  // also already reachable for a patient with nothing overdue at all.
  const hasNoAlerts = alertsLoaded && alertDocuments.length === 0;
  const signatureOnlyAlerts = alertsLoaded && !hasBlockingDocAlert && !hasNoAlerts;
  // Three ways past the modal, NONE requiring the override permission:
  //   - the ReCheck was already approved, so the discharge is cleared to proceed — including for
  //     the therapist who actually writes it, who typically cannot override;
  //   - nothing blocking is outstanding, i.e. every alert is a pending signature; or
  //   - nothing is outstanding at all.
  // With a blocking document outstanding and no approval, a user who cannot override is still
  // limited to dismissing this modal — that gate is the point of the feature and is unchanged.
  const canProceed = isApproved || signatureOnlyAlerts || hasNoAlerts;
  // Signature-only alerts (and an empty list) need no approval from anyone, so skip the approve
  // flow entirely rather than asking an override-capable user to sign off on something
  // non-blocking.
  const showRecheckOverride = canOverride && !isApproved && !signatureOnlyAlerts && !hasNoAlerts;

  useEffect(() => {
    dispatch(recheckDischargeOverrideStatusAction());
    // Clear any stale approve result so a previous success doesn't auto-proceed.
    dispatch(resetApproveRecheckDischarge());
    // No reset on unmount for the override status: the documents list shares it to
    // toggle the "ReCheck for Discharge" button, so it must survive the modal closing.
  }, [source, dispatch]);

  // Records the recheck-discharge approval on the BE; on success proceed to discharge.
  // THIN-7875: this runs for every entry point now, not just EOC. The dashboard used to navigate
  // straight from "Approve Discharge" without ever calling the BE, so the discharge proceeded with
  // no recheck_discharge_approved_by / _at on the cycle — no record that anyone accepted the gap.
  // Without an episode of care there is nothing to stamp, so fall back to proceeding rather than
  // POSTing to /approve-recheck-discharge/undefined.
  const handleApprove = () => {
    if (!episodeOfCareId) {
      handleContinue();
      return;
    }
    dispatch(approveRecheckDischargeAction({ eocId: episodeOfCareId, discipline }));
  };

  useEffect(() => {
    if (approveState?.isSuccess) {
      handleContinue();
    }
  }, [approveState?.isSuccess]);

  const [agree, setAgree] = useState(false);
  const handleChangesStatus = (e: any) => {
    setAgree(e?.target?.checked);
  };

  const handleContinue = () => {
    if (isRecheckFlow) {
      handleDelete();
      return;
    }
    if (source === 'DASHBOARD') {
      navigate('/document/document-edit', {
        state: {
          document: document,
        },
      });
    } else {
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
        state: {
          document,
          activeTab,
          patientData,
          episodeOfCareId,
          requiredDocument: requiredDocument,
        },
      });
    }
  };

  return (
    <Box
      p={3}
      data-testid={eocTestId('discharge-dialog')}
    >
      <>
        <DialogContent>
          <Box sx={{ display: 'flex' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <img
                  alt="recheck"
                  src={ReCheckIcon}
                />
                <Typography variant="h5">{title}</Typography>
              </Box>
              <Typography
                sx={{ color: '#6C737F', fontWeight: '400', width: '100%' }}
                variant="body2"
                mt={0.2}
              >
                {message}
              </Typography>
            </Box>
          </Box>
          {!hasNoAlerts && (
            <Box mt={3}>
              <MissingDocsList />
            </Box>
          )}
          {showRecheckOverride ? (
            <Stack
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              mt={3}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agree}
                    inputProps={
                      {
                        'data-testid': eocTestId('discharge-acknowledge'),
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                    onChange={handleChangesStatus}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    flexWrap="wrap"
                  >
                    I&apos;ve reviewed the alerts and approve to discharge
                  </Typography>
                }
              />
            </Stack>
          ) : !canProceed ? (
            <Typography
              variant="body1"
              fontWeight="600"
              flexWrap="wrap"
              mt={2}
            >
              Contact your manager to review the alerts and approve the discharge.
            </Typography>
          ) : signatureOnlyAlerts && !isApproved ? (
            <Typography
              variant="body1"
              fontWeight="600"
              flexWrap="wrap"
              mt={2}
            >
              These signatures are still outstanding and stay on the signing queue. You can continue
              with the discharge.
            </Typography>
          ) : hasNoAlerts && !isApproved ? (
            <Typography
              variant="body1"
              fontWeight="600"
              flexWrap="wrap"
              mt={2}
            >
              No outstanding documentation is holding up this discharge. You can continue.
            </Typography>
          ) : isApproved ? (
            <Typography
              variant="body1"
              fontWeight="600"
              flexWrap="wrap"
              mt={2}
            >
              This discharge has already been approved{approvedBy ? ` by ${approvedBy}` : ''}.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          {showRecheckOverride ? (
            <>
              <Button
                variant="text"
                data-testid={eocTestId('discharge-cancel')}
                onClick={handleDelete}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                data-testid={eocTestId('discharge-approve')}
                onClick={handleApprove}
                loading={approveState?.loading}
                disabled={!agree || approveState?.loading}
              >
                {'Approve Discharge'}
              </LoadingButton>
            </>
          ) : (
            <Button
              autoFocus
              variant="contained"
              data-testid={eocTestId('discharge-continue')}
              onClick={canProceed ? handleContinue : handleDelete}
            >
              {(signatureOnlyAlerts || hasNoAlerts) && !isApproved
                ? 'Continue to Discharge'
                : buttontext}
            </Button>
          )}
        </DialogActions>
      </>
    </Box>
  );
};

export default DischargeRecheckModal;
